<template>
	<div class="subscription-container">
		<section v-if="error && !appleAccountMismatch" class="error-section">
			<div class="error-container">
				<p class="error-title">{{ errorTitle }}</p>
				<p class="error-message">{{ error }}</p>
			</div>
		</section>

		<section v-if="loading" class="loading-section">
			<div class="spinner"></div>
			<p>Loading App Store subscription information...</p>
		</section>

		<section v-else class="subscription-info">
			<h2 class="subscription-title">Your Subscription</h2>

			<div class="current-plan" :class="{ 'free-plan': !isPlusPlan }">
				<div class="plan-badge" :class="planBadgeClass">
					{{ planBadgeText }}
				</div>

				<div class="plan-details">
					<h3>{{ planName }}</h3>
					<p>{{ planMessage }}</p>
					<p v-if="showEndDate">
						Subscription ends: {{ new Date(endDate).toLocaleDateString() }}
					</p>

					<div v-if="activeSubscription" class="subscription-meta">
						<p class="purchase-date">
							Purchased: {{ formatDate(activeSubscription.purchaseTime) }}
						</p>
						<p class="renewal-status">
							{{
								activeSubscription.isAutoRenewing
									? "Auto-renews in App Store"
									: "Cancels at period end"
							}}
						</p>
					</div>
				</div>
			</div>

			<div v-if="appleAccountMismatch" class="account-notice">
				<p class="notice-title">App Store purchases are unavailable here</p>
				<p>
					The Apple account on this device already has a GrubVision subscription
					linked to a different GrubVision login.
				</p>
				<p>
					To use Apple billing with this GrubVision account, switch this device
					to the Apple account that matches it. You can also subscribe from Grub
					Vision on the web using this account.
				</p>
			</div>

			<div v-if="isPlusPlan && !appleAccountMismatch" class="action-buttons">
				<button
					v-if="platformActive === 'ios'"
					class="manage-button"
					@click="manageActiveSubscription()">
					Manage Subscription
				</button>
				<button
					v-if="platformActive === 'ios'"
					class="refresh-button"
					@click="refreshAppStoreStatus">
					Restore Purchases
				</button>
				<p v-else class="subscription-message">{{ plusActionMessage }}</p>
			</div>

			<template v-if="!isPlusPlan">
				<h3 v-if="!appleAccountMismatch" class="upgrade-title">Upgrade to plus</h3>

				<div v-if="showPlansLoading" class="loading-plans">
					<div class="spinner small"></div>
					<p>Loading App Store plans...</p>
				</div>

				<div v-else-if="hasSubscriptionDetails" class="subscription-offers">
					<div v-for="(details, index) in subscriptionDetails" :key="index">
						<div
							v-for="(offer, offerIdx) in details.subscriptionOfferDetails"
							:key="offerIdx"
							class="product-card"
							:class="{ 'disabled-product': appleAccountMismatch }">
							<div class="description">
								<h3>{{ getPlanTitle(details, offer) }}</h3>
								<p class="product-id">Product ID: {{ details.productId }}</p>
								<div
									v-if="offer.pricingPhases && offer.pricingPhases.length > 0"
									class="price-container">
									<h5>
										{{ offer.pricingPhases[0].formattedPrice }} /
										{{ formatPeriod(offer.pricingPhases[0].billingPeriod) }}
									</h5>
									<p v-if="!appleAccountMismatch" class="renewal-summary">
										{{ getRenewalSummary(offer) }}
									</p>
									<p class="product-description">
										{{ getPlanDescription(details, offer) }}
									</p>
								</div>
							</div>

							<button
								class="checkout-button"
								@click="subscribe(details.productId)"
								:disabled="
									appleAccountMismatch || isOfferProcessing(details.productId)
								">
								<span
									v-if="isOfferProcessing(details.productId)"
									class="spinner small"></span>
								{{ appleAccountMismatch ? "Unavailable" : "Subscribe" }}
							</button>
						</div>
					</div>
				</div>

				<div
					v-else-if="billingDetailsLoaded && !appleAccountMismatch"
					class="unavailable-plans">
					<p>App Store plans are currently unavailable.</p>
					<p>Please try refreshing purchases or check back later.</p>
				</div>

				<div class="subscription-legal">
					<p>{{ renewalDisclosure }}</p>
					<p>
						By subscribing, you agree to the
						<router-link to="/terms">Terms of Use</router-link>
						and
						<a :href="appleEulaUrl" target="_blank" rel="noopener"
							>Apple Standard EULA</a
						>,
						and acknowledge the
						<router-link to="/privacy-policy">Privacy Policy</router-link>.
					</p>
				</div>

				<div v-if="!appleAccountMismatch" class="secondary-actions">
					<button class="refresh-button" @click="refreshAppStoreStatus">
						{{ restoreButtonText }}
					</button>
				</div>
			</template>

			<div v-if="appleAccountMismatch" class="secondary-actions">
				<button class="refresh-button" @click="refreshAppStoreStatus">
					Restore Purchases
				</button>
			</div>
		</section>

		<div
			v-if="restoreDialog.visible"
			class="restore-dialog-backdrop"
			@click.self="closeRestoreDialog">
			<div
				class="restore-dialog"
				:class="`restore-dialog-${restoreDialog.type}`"
				role="dialog"
				aria-modal="true"
				aria-labelledby="restore-dialog-title">
				<div class="restore-orbit" aria-hidden="true">
					<span class="restore-orbit-ring"></span>
					<span class="restore-icon">{{ restoreDialogIcon }}</span>
				</div>

				<div class="restore-dialog-content">
					<p class="restore-kicker">{{ restoreDialog.kicker }}</p>
					<h3 id="restore-dialog-title">{{ restoreDialog.title }}</h3>
					<p>{{ restoreDialog.message }}</p>

					<div
						v-if="restoreDialog.type === 'success'"
						class="restore-details">
						<div>
							<span>Plan</span>
							<strong>{{ restoreDialog.plan }}</strong>
						</div>
						<div>
							<span>Renews until</span>
							<strong>{{ restoreDialog.expires }}</strong>
						</div>
						<div>
							<span>Product ID</span>
							<strong>{{ restoreDialog.productId }}</strong>
						</div>
					</div>

					<button class="restore-dialog-button" @click="closeRestoreDialog">
						Done
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { computed, ref, onMounted, watch } from "vue"
	import { useBilling } from "@/plugins/Billing/useBilling"
	import { useUserStore } from "@/stores/user"
	import { fetchWithAuth } from "@/utils/api-client"

	const SUBSCRIPTION_PRODUCT_IDS = [
		"grubvision.plus.monthly1",
		"grubvision.plus.yearly",
	]
	const appleEulaUrl =
		"https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
	const renewalDisclosure =
		"GrubVision Plus is an auto-renewable subscription. Payment is charged to your Apple ID, and your subscription renews automatically unless canceled at least 24 hours before the end of the current period. You can manage or cancel in App Store account settings."

	const {
		isReady,
		subscriptions,
		subscriptionDetails,
		loadSubscriptionDetails,
		loadSubscriptions,
		purchaseSubscription,
		manageSubscription,
	} = useBilling({ loadSubscriptionsOnReady: false })

	const userStore = useUserStore()
	const processingOffers = ref(new Map())
	const loading = ref(true)
	const error = ref(null)
	const restoreDialog = ref({
		visible: false,
		type: "success",
		kicker: "",
		title: "",
		message: "",
		plan: "",
		expires: "",
		productId: "",
	})
	const subscriptionType = ref("basic")
	const platformActive = ref(null)
	const billingDetailsLoaded = ref(false)
		const endDate = ref(null)
		const appleAppAccountToken = ref("")
		const appleAccountMismatch = ref(false)
		const isPurchaseCancelled = computed(() => {
			return typeof error.value === "string" && isPurchaseCancellationMessage(error.value)
		})
	const errorTitle = computed(() =>
		isPurchaseCancelled.value ? "Purchase cancelled" : "Something went wrong"
	)

	const isPurchaseCancellationMessage = (message) => {
		return message.toLowerCase().includes("purchase cancelled")
	}

		const isPurchaseCancellationError = (err) => {
			return (
				err?.code === "1" ||
				err?.code === 1 ||
			(typeof err?.message === "string" &&
				isPurchaseCancellationMessage(err.message))
			)
		}

		const isAppleAccountMismatchError = (err) => {
			const message = String(err?.message || "").toLowerCase()
			return (
				err?.status === 403 ||
				err?.status === 409 ||
				message.includes("app account token does not match") ||
				message.includes("already linked to another")
			)
		}

		const showAppleAccountMismatch = () => {
			appleAccountMismatch.value = true
			error.value = null
		}

	watch(isReady, async (ready) => {
		if (ready) {
			await loadIOSSubscriptionDetails()
		}
	})

	const fetchSubscriptionInfo = async () => {
		try {
			loading.value = true
			const response = await fetchWithAuth("/api/get-subscription")

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`)
			}

			const data = await response.json()
			subscriptionType.value = data.subscriptionType
			platformActive.value = data.platformActive
			endDate.value = data.endDate
			appleAppAccountToken.value = data.appleAppAccountToken || ""

			if (userStore.user) {
				userStore.user.subscription_type = data.subscriptionType
				userStore.user.platform_active = data.platformActive
			}

			return data
		} catch (err) {
			error.value = err.message || "Failed to load subscription information"
			console.error("Error fetching subscription info:", err)
			return null
		} finally {
			loading.value = false
		}
	}

	const getPlatformName = (platform) => {
		switch (platform) {
			case "web":
				return "Web Browser"
			case "android":
				return "Google Play Store"
			default:
				return platform
		}
	}

	const loadIOSSubscriptionDetails = async () => {
		try {
			appleAccountMismatch.value = false
			await fetchSubscriptionInfo()

			if (subscriptionType.value === "basic") {
				await loadSubscriptionDetails(SUBSCRIPTION_PRODUCT_IDS)
			}

			billingDetailsLoaded.value = true
		} catch (err) {
				billingDetailsLoaded.value = true
				if (isAppleAccountMismatchError(err)) {
					showAppleAccountMismatch()
				} else {
					console.error("Failed to load iOS subscription data:", err)
					error.value = err.message || "Failed to load App Store plans"
				}
			}
		}

	const syncApplePurchase = async (purchase) => {
		const response = await fetchWithAuth("/api/apple-billing", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				signedTransactionInfo: purchase.signedTransactionInfo,
				signedRenewalInfo: purchase.signedRenewalInfo || null,
			}),
		})

			if (!response.ok) {
				const data = await response.json().catch(() => null)
				const syncError = new Error(
					data?.error || "Failed to sync App Store purchase"
				)
				syncError.status = response.status
				throw syncError
			}

		return response.json()
	}

	const syncNoActiveAppleSubscription = async () => {
		const response = await fetchWithAuth("/api/apple-billing", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				restoreAttempted: true,
				activeAppleSubscription: false,
			}),
		})

		if (!response.ok) {
			const data = await response.json().catch(() => null)
			const syncError = new Error(
				data?.error || "Failed to sync App Store restore result"
			)
			syncError.status = response.status
			throw syncError
		}

		return response.json()
	}

	const closeRestoreDialog = () => {
		restoreDialog.value.visible = false
	}

	const showRestoreSuccessDialog = (purchase, subscriptionInfo) => {
		const productId = purchase?.sku || "Unknown"

		restoreDialog.value = {
			visible: true,
			type: "success",
			kicker: "Purchase restored",
			title: "GrubVision Plus is back on this account",
			message:
				"Your active App Store subscription was found and linked to your current GrubVision login.",
			plan: getPlanNameForProduct(productId),
			expires: subscriptionInfo?.endDate
				? new Date(subscriptionInfo.endDate).toLocaleDateString()
				: "Active",
			productId,
		}
	}

	const showRestoreEmptyDialog = () => {
		restoreDialog.value = {
			visible: true,
			type: "empty",
			kicker: "Nothing active",
			title: "No active App Store purchases found",
			message:
				"This Apple account has no purchases on it that are active. Your GrubVision account is on the free plan.",
			plan: "",
			expires: "",
			productId: "",
		}
	}

		const subscribe = async (productId) => {
			if (appleAccountMismatch.value) {
				return
			}

			error.value = null
			processingOffers.value.set(productId, true)

		try {
			const purchase = await purchaseSubscription(
				productId,
				productId,
				appleAppAccountToken.value
			)
			await syncApplePurchase(purchase)
			await fetchSubscriptionInfo()
			} catch (err) {
				if (isPurchaseCancellationError(err)) {
					error.value = null
				} else if (isAppleAccountMismatchError(err)) {
					showAppleAccountMismatch()
				} else {
					console.error("App Store subscription failed:", err)
					error.value = err.message || "Subscription failed"
			}
		} finally {
			processingOffers.value.delete(productId)
		}
	}

	const refreshAppStoreStatus = async () => {
		error.value = null
		appleAccountMismatch.value = false
		try {
			const restoreResult = await loadSubscriptions(SUBSCRIPTION_PRODUCT_IDS, true)
			const activePurchase = getActiveSubscription()

			if (!activePurchase?.signedTransactionInfo) {
				if (restoreResult?.restoreSyncFailed) {
					throw new Error(
						restoreResult.restoreSyncError ||
							"App Store restore could not confirm your purchase history. Please try again."
					)
				}

				await syncNoActiveAppleSubscription()
				await fetchSubscriptionInfo()
				showRestoreEmptyDialog()
				return
			}

			await syncApplePurchase(activePurchase || {})
			const subscriptionInfo = await fetchSubscriptionInfo()
			showRestoreSuccessDialog(activePurchase, subscriptionInfo)
		} catch (err) {
			if (isAppleAccountMismatchError(err)) {
				showAppleAccountMismatch()
			} else {
				error.value = err.message || "Failed to refresh App Store purchases"
			}
		}
	}

	const isOfferProcessing = (productId) => {
		return processingOffers.value.has(productId)
	}

	const getActiveSubscription = () => {
		const activeSku = Object.keys(subscriptions).find((sku) => {
			const purchase = subscriptions[sku]
			if (!purchase) return false
			if (purchase.isAutoRenewing) return true
			if (!purchase.purchaseTime) return false

			return purchase.purchaseState === 1
		})

		return activeSku ? subscriptions[activeSku] : null
	}

	const isPlusPlan = computed(() => subscriptionType.value === "plus")
	const hasSubscriptionDetails = computed(
		() => Object.keys(subscriptionDetails).length > 0
	)
	const activeSubscription = computed(() =>
		isPlusPlan.value ? getActiveSubscription() : null
	)
	const planBadgeClass = computed(() => (isPlusPlan.value ? "plus" : "basic"))
	const planBadgeText = computed(() => (isPlusPlan.value ? "PLUS" : "FREE"))
	const planName = computed(() =>
		isPlusPlan.value ? "GrubVision Plus" : "GrubVision Free Plan"
	)
	const showEndDate = computed(
		() => Boolean(endDate.value) && (isPlusPlan.value || appleAccountMismatch.value)
	)
	const showPlansLoading = computed(
		() =>
			!appleAccountMismatch.value &&
			!hasSubscriptionDetails.value &&
			!billingDetailsLoaded.value
	)
	const restoreButtonText = computed(() => "Restore Purchases")
	const restoreDialogIcon = computed(() =>
		restoreDialog.value.type === "success" ? "✓" : "!"
	)
	const planMessage = computed(() => {
		if (platformActive.value === "trial") {
			return "You are currently using a GrubVision Plus trial."
		}

		if (isPlusPlan.value) {
			return platformActive.value === "ios"
				? "You are currently subscribed through the App Store."
				: "You currently have Plus on this GrubVision account."
		}

		return appleAccountMismatch.value
			? "You are currently using the free plan."
			: "You are currently using the free plan. Upgrade in the App Store to unlock Plus."
	})
	const plusActionMessage = computed(() => {
		switch (platformActive.value) {
			case "trial":
				return "Enjoy the trial period and explore the features."
			case "gifted_feedback":
				return "Enjoy the subscription that was gifted to you for being a tester."
			case "gifted_forever":
				return "You have a lifelong subscription on the house!"
			case null:
				return "Your subscription information cannot be managed at this time"
			default:
				return `Your subscription is managed through ${getPlatformName(
					platformActive.value
				)}`
		}
	})

	const manageActiveSubscription = async () => {
		const currentSubscription = getActiveSubscription()
		await manageSubscription(currentSubscription?.sku)
	}

	const getPlanTitle = (details, offer) => {
		if (!offer.pricingPhases || !offer.pricingPhases.length) {
			return details.name || "Plus"
		}

		const period = offer.pricingPhases[0].billingPeriod
		if (period.includes("Y")) {
			return "Plus Yearly"
		}
		if (period.includes("M")) {
			return "Plus Monthly"
		}

		return details.name || "Plus"
	}

	const getPlanDescription = (details, offer) => {
		if (!offer.pricingPhases || !offer.pricingPhases.length) {
			return details.description || "Plus subscription"
		}

		const period = offer.pricingPhases[0].billingPeriod
		if (period.includes("Y")) {
			return "Best value plan with all plus features"
		}
		if (period.includes("M")) {
			return "Flexible monthly subscription"
		}

		return details.description || "Plus subscription"
	}

	const getPlanNameForProduct = (productId) => {
		if (!productId) return "GrubVision Plus"
		if (productId.includes("year")) return "GrubVision Plus Yearly"
		if (productId.includes("month")) return "GrubVision Plus Monthly"
		return "GrubVision Plus"
	}

	const getRenewalSummary = (offer) => {
		const phase = offer.pricingPhases?.[0]
		if (!phase) {
			return "Auto-renewable subscription"
		}

		const period = formatPeriod(phase.billingPeriod)
		return `Auto-renews every ${period} at ${phase.formattedPrice} until canceled.`
	}

	const formatDate = (timestamp) => {
		if (!timestamp) return "Unknown"
		return new Date(timestamp).toLocaleDateString()
	}

	const formatPeriod = (period) => {
		if (!period) return ""

		const match = period.match(/P(\d+)([DWMY])/)
		if (!match) return period

		const [_, count, unit] = match
		const unitMap = {
			D: "day",
			W: "week",
			M: "month",
			Y: "year",
		}

		const unitName = unitMap[unit] || unit
		return `${count} ${unitName}${Number(count) > 1 ? "s" : ""}`
	}

	onMounted(() => {
		if (isReady.value) {
			loadIOSSubscriptionDetails()
		}
	})
</script>

<style lang="less" scoped>
	.subscription-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		max-width: 100%;
		margin: 0 auto;
	}

	section {
		background: #ffffff;
		display: flex;
		flex-direction: column;
		width: min(720px, 100%);
		border-radius: 20px;
		padding: 1.5rem;
		box-shadow: 0 12px 36px rgba(15, 23, 42, 0.08);
		margin-bottom: 1.5rem;
	}

	.subscription-title,
	.upgrade-title {
		text-align: center;
		color: #32325d;
	}

	.current-plan {
		display: flex;
		gap: 1rem;
		align-items: flex-start;
		padding: 1rem;
		border-radius: 16px;
		background: linear-gradient(135deg, #f8fafc, #eef2ff);
	}

	.plan-badge {
		min-width: 72px;
		padding: 0.5rem 0.75rem;
		border-radius: 999px;
		text-align: center;
		font-weight: 700;
		color: white;
	}

	.plan-badge.plus {
		background: linear-gradient(135deg, #2563eb, #1d4ed8);
	}

	.plan-badge.basic {
		background: linear-gradient(135deg, #64748b, #475569);
	}

	.plan-details {
		flex: 1;
		color: #334155;
	}

	.subscription-meta,
	.action-buttons,
	.secondary-actions,
	.subscription-offers {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		margin-top: 1rem;
	}

		.product-card {
			display: flex;
			justify-content: space-between;
		align-items: center;
		gap: 1rem;
		padding: 1rem;
		border: 1px solid #dbe4ff;
			border-radius: 16px;
		}

		.disabled-product {
			opacity: 0.75;
		}

	.description {
		color: #334155;
	}

	.product-id {
		margin: 0.15rem 0 0.45rem;
		color: #64748b;
		font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
			"Liberation Mono", "Courier New", monospace;
		font-size: 0.78rem;
		overflow-wrap: anywhere;
	}

	.renewal-summary,
	.subscription-legal,
	.unavailable-plans {
		color: #475569;
		font-size: 0.92rem;
		line-height: 1.45;
	}

	.subscription-legal,
	.unavailable-plans {
		margin-top: 1rem;
		padding: 1rem;
		border: 1px solid #e2e8f0;
		border-radius: 8px;
		background: #f8fafc;
	}

	.subscription-legal p,
	.unavailable-plans p {
		margin: 0.35rem 0;
	}

	.subscription-legal a {
		color: #1d4ed8;
		font-weight: 600;
	}

	.restore-dialog-backdrop {
		position: fixed;
		inset: 0;
		z-index: 1000;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.25rem;
		background: rgba(15, 23, 42, 0.58);
		backdrop-filter: blur(10px);
	}

	.restore-dialog {
		position: relative;
		width: min(440px, 100%);
		overflow: hidden;
		border-radius: 20px;
		background: #ffffff;
		box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
	}

	.restore-dialog::before {
		content: "";
		position: absolute;
		inset: 0 0 auto;
		height: 6px;
		background: linear-gradient(90deg, #0ea5e9, #22c55e);
	}

	.restore-dialog-empty::before {
		background: linear-gradient(90deg, #64748b, #94a3b8);
	}

	.restore-orbit {
		position: absolute;
		top: 1.2rem;
		right: 1.2rem;
		width: 72px;
		height: 72px;
		display: grid;
		place-items: center;
	}

	.restore-orbit-ring {
		position: absolute;
		inset: 0;
		border-radius: 50%;
		background: conic-gradient(from 20deg, #0ea5e9, #22c55e, #0ea5e9);
		opacity: 0.22;
	}

	.restore-dialog-empty .restore-orbit-ring {
		background: conic-gradient(from 20deg, #64748b, #cbd5e1, #64748b);
	}

	.restore-icon {
		position: relative;
		width: 46px;
		height: 46px;
		display: grid;
		place-items: center;
		border-radius: 50%;
		background: #ecfdf5;
		color: #047857;
		font-size: 1.5rem;
		font-weight: 800;
	}

	.restore-dialog-empty .restore-icon {
		background: #f1f5f9;
		color: #475569;
	}

	.restore-dialog-content {
		position: relative;
		padding: 2rem;
		padding-right: 6.2rem;
		color: #0f172a;
	}

	.restore-kicker {
		margin: 0 0 0.45rem;
		color: #2563eb;
		font-size: 0.78rem;
		font-weight: 800;
		text-transform: uppercase;
		letter-spacing: 0.08em;
	}

	.restore-dialog-empty .restore-kicker {
		color: #64748b;
	}

	.restore-dialog h3 {
		margin: 0 0 0.65rem;
		font-size: 1.35rem;
		line-height: 1.15;
	}

	.restore-dialog p {
		margin: 0;
		color: #475569;
		line-height: 1.5;
	}

	.restore-details {
		display: grid;
		gap: 0.65rem;
		margin-top: 1.25rem;
		padding: 1rem;
		border: 1px solid #dbeafe;
		border-radius: 14px;
		background: #f8fafc;
	}

	.restore-details div {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
	}

	.restore-details span {
		color: #64748b;
		font-size: 0.82rem;
	}

	.restore-details strong {
		min-width: 0;
		color: #0f172a;
		font-size: 0.88rem;
		text-align: right;
		overflow-wrap: anywhere;
	}

	.restore-dialog-button {
		width: 100%;
		margin-top: 1.35rem;
		border: none;
		border-radius: 999px;
		padding: 0.85rem 1.1rem;
		background: #0f172a;
		color: #ffffff;
		font-weight: 800;
	}

	.checkout-button,
	.manage-button,
	.refresh-button {
		border: none;
		border-radius: 999px;
		padding: 0.8rem 1.2rem;
		font-weight: 600;
	}

	.checkout-button,
	.manage-button {
		background: #2563eb;
		color: white;
	}

	.refresh-button {
		background: #e2e8f0;
		color: #0f172a;
	}

	.loading-section,
	.loading-plans,
	.error-container {
		align-items: center;
		text-align: center;
		color: #334155;
	}

	.spinner {
		width: 28px;
		height: 28px;
		border-radius: 50%;
		border: 3px solid rgba(37, 99, 235, 0.18);
		border-top-color: #2563eb;
		animation: spin 1s linear infinite;
	}

	.spinner.small {
		width: 18px;
		height: 18px;
		border-width: 2px;
		display: inline-block;
		vertical-align: middle;
		margin-right: 0.4rem;
	}

	.error-title {
		font-weight: 700;
		color: #b91c1c;
	}

		.error-message {
			color: #7f1d1d;
		}

		.account-notice {
			margin: 0.75rem 0 1rem;
			padding: 1rem;
			border: 1px solid #bfdbfe;
			border-radius: 8px;
			background: #eff6ff;
			color: #1e3a8a;
		}

		.account-notice p {
			margin: 0.35rem 0;
		}

		.notice-title {
			font-weight: 700;
		}

		button:disabled {
			background: #94a3b8;
			cursor: not-allowed;
		}

		@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

	@media (max-width: 640px) {
		.product-card,
		.current-plan {
			flex-direction: column;
			align-items: stretch;
		}

		.restore-dialog-content {
			padding: 1.5rem;
			padding-top: 5.6rem;
		}

		.restore-orbit {
			left: 1.5rem;
			right: auto;
		}
	}
</style>
